import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useOnboarding } from '../../hooks/useOnboarding';
import OnboardingStep from './OnboardingStep';
import GuidingArrow from './GuidingArrow';
import { OnboardingStepData } from './types';
import '../../styles/onboarding.css';

export const OnboardingPopup: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { shouldShow, isLoading, completeOnboarding } = useOnboarding();
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  // Debug logging
  useEffect(() => {
    console.log('OnboardingPopup - isAuthenticated:', isAuthenticated, 'isLoading:', isLoading, 'shouldShow:', shouldShow);
  }, [isAuthenticated, isLoading, shouldShow]);

  // Highlight target element when showing arrow
  useEffect(() => {
    if (!shouldShow) return;

    const steps: OnboardingStepData[] = [
      { title: '', description: '', visual: null },
      { title: '', description: '', visual: 'profile' },
      { title: '', description: '', visual: 'chat' },
      { title: '', description: '', visual: 'jobtracking' },
      { title: '', description: '', visual: null },
    ];

    const currentVisual = steps[currentStep]?.visual;

    if (currentVisual) {
      const targetEl = document.querySelector(
        `[data-onboarding-target="${currentVisual}-menu"]`
      );

      if (targetEl) {
        targetEl.classList.add('onboarding-highlight');

        return () => {
          targetEl.classList.remove('onboarding-highlight');
        };
      }
    }
  }, [currentStep, shouldShow]);

  // Only show for authenticated users
  if (!isAuthenticated || isLoading || !shouldShow) {
    return null;
  }

  const steps: OnboardingStepData[] = [
    {
      title: 'Welcome to Jobesta!',
      description:
        "Let's get you started on your job search journey in 4 simple steps.",
      visual: null,
    },
    {
      title: 'Step 1: Upload Your CV',
      description:
        'Upload your CV and let our AI analyze it to automatically build your professional profile. This makes everything else easier!',
      visual: 'profile',
      primaryAction: {
        label: 'Go to Profile',
        onClick: () => {
          console.log('Go to Profile clicked, current path:', location.pathname);
          if (location.pathname === '/profile') {
            console.log('Already on profile page, advancing to next step');
            handleNext();
          } else {
            console.log('Navigating to /profile');
            navigate('/profile');
          }
        },
      },
    },
    {
      title: 'Step 2: Enhance Your Profile',
      description:
        'Chat with our AI assistant and ask: "Help me improve my professional profile" to get AI-powered suggestions for your experience.',
      visual: 'chat',
      primaryAction: {
        label: 'Try It Now',
        onClick: () => {
          console.log('Try It Now clicked, current path:', location.pathname);
          if (location.pathname === '/chat') {
            console.log('Already on chat page, advancing to next step');
            // Set the message even if already on chat page
            sessionStorage.setItem(
              'onboarding_chat_message',
              'Help me improve my professional profile'
            );
            handleNext();
          } else {
            // Navigate to chat and set a flag for pre-filled message
            sessionStorage.setItem(
              'onboarding_chat_message',
              'Help me improve my professional profile'
            );
            navigate('/chat');
          }
        },
      },
    },
    {
      title: 'Step 3: Search for Jobs',
      description:
        'Search for jobs in the chat, click Apply, then visit Job Tracker to generate tailored CVs and cover letters for each application.',
      visual: 'jobtracking',
      primaryAction: {
        label: 'Start Searching',
        onClick: () => {
          console.log('Start Searching clicked, current path:', location.pathname);
          if (location.pathname === '/chat') {
            console.log('Already on chat page, advancing to next step');
            handleNext();
          } else {
            navigate('/chat');
          }
        },
      },
    },
    {
      title: "You're Ready!",
      description:
        "You're all set! Start your job search journey with confidence. Remember, you can always ask the AI assistant for help.",
      visual: null,
    },
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep((prev) => prev + 1);
    } else {
      completeOnboarding();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleSkip = () => {
    completeOnboarding();
  };

  const currentStepData = steps[currentStep];

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[9999] p-4 onboarding-overlay"
        onClick={handleSkip}
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-title"
      >
        {/* Modal */}
        <div
          className="bg-white rounded-jobesta-lg shadow-xl w-full max-w-md mx-4 md:mx-0 onboarding-modal"
          onClick={(e) => e.stopPropagation()} // Prevent closing when clicking modal
        >
          <OnboardingStep
            step={currentStep}
            totalSteps={steps.length}
            title={currentStepData.title}
            description={currentStepData.description}
            visual={currentStepData.visual}
            primaryAction={currentStepData.primaryAction}
            secondaryAction={currentStepData.secondaryAction}
            onNext={handleNext}
            onSkip={handleSkip}
            onPrevious={currentStep > 0 ? handlePrevious : undefined}
          />
        </div>
      </div>

      {/* Guiding Arrow */}
      {currentStepData.visual && (
        <GuidingArrow target={`${currentStepData.visual}-menu`} />
      )}
    </>
  );
};

export default OnboardingPopup;
